import { create } from "zustand"
import { supabase } from "@/lib/supabase"
import type { Json } from "@/types/database"
import type { OrgCustomizations, PageSection } from "@/templates/types"
import {
  syncStoredTokensWithLegacy,
  legacyColumnsFromTokens,
  type StoredDesignTokens,
} from "@/lib/designTokens"
import { planThemeApplication, applyThemeVariants, type ExistingSection } from "@/lib/themePackages"
import type { ThemePackage } from "@/types/theme"
import { queuePublicCachePurge, type PurgeTrigger } from "@/lib/publicCache"

export interface CustomizationOrg {
  id: string
  name: string
  slug: string
  mission_statement: string | null
  logo_url: string | null
}

export interface PageSummary {
  id: string
  slug: string
  title: string | null
  is_published: boolean
  page_type?: string
}

/** NOS-154: page-scoped undo/redo history entries. */
export interface SectionUndoEntry {
  kind: "section"
  sectionId: string
  /** Content before the edit. null = the section did not exist (it was added). */
  before: Record<string, unknown> | null
  /** Content after the edit. null = the section was deleted. */
  after: Record<string, unknown> | null
  /** Full section row snapshot for recreation when before or after is null (add/delete). */
  row?: PageSection
  /** Timestamp used to coalesce rapid consecutive edits to the same section. */
  at: number
}

export interface OrderUndoEntry {
  kind: "order"
  before: Array<{ id: string; order_index: number }>
  after: Array<{ id: string; order_index: number }>
  at: number
}

export interface TokensUndoEntry {
  kind: "tokens"
  before: StoredDesignTokens | null
  after: StoredDesignTokens | null
  at: number
}

export type UndoEntry = SectionUndoEntry | OrderUndoEntry | TokensUndoEntry

/** Max entries kept on the undo stack — oldest dropped beyond this. */
const MAX_HISTORY = 100
/** Consecutive content edits to the same section within this window merge into one entry. */
const COALESCE_MS = 1000

interface CustomizationState {
  org: CustomizationOrg | null
  customizations: OrgCustomizations | null
  pages: PageSummary[]
  sections: PageSection[]
  pageId: string | null
  pageSlug: string | null
  pageTitle: string | null
  isPublished: boolean
  loadError: string | null
  /** Page-scoped undo stack — most recent entry last, bounded to MAX_HISTORY. */
  undoStack: UndoEntry[]
  /** Page-scoped redo stack — cleared whenever a new edit is made. */
  redoStack: UndoEntry[]

  /** Load org, customizations, all pages, and a specific page. pageSlugOrId defaults to "home" for backward compat. */
  load: (orgId: string, pageSlugOrId?: string) => Promise<void>
  setCurrentPage: (page: PageSummary | null) => Promise<void>
  addPage: (page: PageSummary) => void
  removePage: (pageId: string) => Promise<void>
  updateColor: (key: "primary" | "secondary", color: string) => Promise<void>
  updateFont: (font: string) => Promise<void>
  updateHeadingFont: (font: string) => Promise<void>
  updateBodyFont: (font: string) => Promise<void>
  updateFontSize: (size: "small" | "medium" | "large") => Promise<void>
  /** Merge a partial token patch into design_tokens; write-through syncs overlapping legacy columns. */
  updateDesignTokens: (patch: StoredDesignTokens) => Promise<void>
  updateCustomCss: (css: string) => Promise<void>
  updateNavCtaText: (text: string) => Promise<void>
  updateLogoScale: (scale: number) => Promise<void>
  updateStickyNav: (sticky: boolean) => Promise<void>
  /** NOS-239: site-wide announcement bar config (null clears it). */
  updateAnnouncement: (config: Record<string, unknown> | null) => Promise<void>
  setOrgLogo: (url: string | null) => void
  updateSection: (sectionId: string, content: Record<string, unknown>) => Promise<void>
  reorderSections: (newOrder: PageSection[]) => Promise<void>
  addSection: (sectionType: string, afterIndex: number) => Promise<PageSection | null>
  removeSection: (sectionId: string) => Promise<void>
  toggleSectionVisibility: (sectionId: string, visible: boolean) => Promise<void>
  /** NOS-116: Mark section as reviewed (removes import badge) */
  markSectionReviewed: (sectionId: string) => Promise<void>
  publishWebsite: () => Promise<void>
  unpublishPage: () => Promise<void>
  /**
   * Push a pre-change snapshot of a section onto the undo stack before a destructive
   * operation. The subsequent updateSection call coalesces into it, so the pair records
   * a single undo entry whose `before` is this snapshot.
   */
  pushUndoHistory: (sectionId: string, content: Record<string, unknown>) => void
  /** Revert the most recent edit (section content/add/delete, order, or design tokens). */
  undo: () => Promise<void>
  /** Re-apply the most recently undone edit. */
  redo: () => Promise<void>

  /** NOS-158: a theme is being previewed on the canvas (in-memory only, nothing persisted). */
  themePreviewActive: boolean
  /** Overlay a theme's tokens + variants on the canvas without persisting. Re-previewing swaps themes; state stays pristine underneath. */
  previewTheme: (pkg: ThemePackage) => void
  /** Discard the preview and restore the exact pre-preview canvas. */
  cancelThemePreview: () => void
  /** Persist the theme: tokens (undoable) + per-type variants on every page. User content is never touched. */
  commitTheme: (pkg: ThemePackage, templateId?: string) => Promise<void>
}

const PAGE_TYPE_ORDER = ["home", "about", "events", "blog", "contact", "custom"]

/** Canonical nav/page ordering (home → about → events → blog → contact → custom, alpha within type).
 * Exported so the public site orders pages identically to the builder. */
export function sortPages<T extends { slug: string | null; page_type?: string | null }>(
  pages: T[]
): T[] {
  return [...pages].sort((a, b) => {
    const typeA = (a.page_type ?? (a.slug === "home" ? "home" : "custom")) as string
    const typeB = (b.page_type ?? (b.slug === "home" ? "home" : "custom")) as string
    const idxA = PAGE_TYPE_ORDER.indexOf(typeA)
    const idxB = PAGE_TYPE_ORDER.indexOf(typeB)
    if (idxA !== idxB) return idxA - idxB
    return (a.slug ?? "").localeCompare(b.slug ?? "")
  })
}

export const useCustomizationStore = create<CustomizationState>((set, get) => {
  /** NOS-158: pristine {customizations, sections} snapshot held while a theme preview is active. */
  let themeBackup: { customizations: OrgCustomizations; sections: PageSection[] } | null = null

  /** NOS-248: fire-and-forget CDN purge for the current org (debounced downstream). */
  const purge = (trigger: PurgeTrigger) => queuePublicCachePurge(get().org?.id, trigger)
  /** Purge the current builder page (falls back to home for the initial single-page flow). */
  const purgePage = (type: "section-reorder" | "section-add" | "section-delete" | "section-visibility") =>
    purge({ type, pageSlug: get().pageSlug ?? "home" })

  /**
   * Push a history entry onto the undo stack. Any new edit clears the redo stack.
   * Consecutive content edits to the same section within COALESCE_MS merge into a
   * single entry (keep original `before`, update `after`) so undo isn't per-keystroke.
   */
  const pushEntry = (entry: UndoEntry) => {
    set((state) => {
      const stack = [...state.undoStack]
      const last = stack[stack.length - 1]
      if (
        entry.kind === "section" &&
        last?.kind === "section" &&
        last.sectionId === entry.sectionId &&
        last.before !== null &&
        last.after !== null &&
        entry.before !== null &&
        entry.after !== null &&
        entry.at - last.at <= COALESCE_MS
      ) {
        stack[stack.length - 1] = { ...last, after: entry.after, at: entry.at }
        return { undoStack: stack, redoStack: [] }
      }
      stack.push(entry)
      return { undoStack: stack.slice(-MAX_HISTORY), redoStack: [] }
    })
  }

  /** Shared persistence path for section content (used by updateSection and undo/redo). */
  const persistSectionContent = async (sectionId: string, content: Record<string, unknown>) => {
    const { sections, org } = get()
    const section = sections.find((s) => s.id === sectionId)
    set((state) => ({
      sections: state.sections.map((s) => (s.id === sectionId ? { ...s, content } : s)),
    }))
    const { error } = await supabase
      .from("page_sections")
      .update({ content: content as Json })
      .eq("id", sectionId)
    if (error) throw new Error(error.message)
    // Footer is a global element — sync to org_customizations so every page and the
    // public site always reflects the same footer regardless of which page it was edited on.
    if (section?.section_type === "footer" && org?.id) {
      await supabase
        .from("org_customizations")
        .upsert({ org_id: org.id, footer_content: content as Json }, { onConflict: "org_id" })
    }
    purge({
      type: "section-save",
      pageSlug: get().pageSlug ?? "home",
      sectionType: section?.section_type,
    })
  }

  /**
   * Two-phase order_index write to avoid UNIQUE(page_id, order_index) violations:
   * 1. all rows to temporary negative order_index, 2. all rows to final order_index.
   */
  const persistOrderDb = async (orders: Array<{ id: string; order_index: number }>) => {
    const sorted = [...orders].sort((a, b) => a.order_index - b.order_index)
    for (let i = 0; i < sorted.length; i++) {
      const { error: phase1Error } = await supabase
        .from("page_sections")
        .update({ order_index: -(i + 1) })
        .eq("id", sorted[i].id)
      if (phase1Error) throw new Error(phase1Error.message)
    }
    for (const order of sorted) {
      const { error: phase2Error } = await supabase
        .from("page_sections")
        .update({ order_index: order.order_index })
        .eq("id", order.id)
      if (phase2Error) throw new Error(phase2Error.message)
    }
    purgePage("section-reorder")
  }

  /** Apply an {id → order_index} mapping to local sections and re-sort. */
  const applyOrderLocal = (orders: Array<{ id: string; order_index: number }>) => {
    const byId = new Map(orders.map((o) => [o.id, o.order_index]))
    set((state) => ({
      sections: state.sections
        .map((s) => (byId.has(s.id) ? { ...s, order_index: byId.get(s.id)! } : s))
        .sort((a, b) => a.order_index - b.order_index),
    }))
  }

  /** Shared persistence path for design tokens (used by updateDesignTokens and undo/redo). */
  const applyDesignTokensState = async (tokens: StoredDesignTokens | null) => {
    const { customizations, org } = get()
    if (!customizations) throw new Error("Customizations not loaded")
    const orgId = org?.id
    if (!orgId) throw new Error("Organization not loaded. Please refresh and try again.")
    const design_tokens = tokens ?? {}
    // Write-through: keep pre-token readers (legacy columns) correct during transition.
    const legacyCols = legacyColumnsFromTokens(design_tokens)
    set({ customizations: { ...customizations, ...legacyCols, design_tokens } })
    const { error } = await supabase
      .from("org_customizations")
      .upsert(
        { org_id: orgId, ...legacyCols, design_tokens: design_tokens as Json },
        { onConflict: "org_id", ignoreDuplicates: false }
      )
    if (error) throw new Error(error.message)
    purge({ type: "design-tokens-change" })
  }

  /** Recreate a deleted section from its row snapshot (undo of delete / redo of add). */
  const recreateSection = async (row: PageSection, content: Record<string, unknown>) => {
    const { error } = await supabase.from("page_sections").insert({
      id: row.id,
      page_id: row.page_id,
      org_id: row.org_id,
      section_type: row.section_type,
      order_index: row.order_index,
      content: content as Json,
      is_visible: row.is_visible ?? true,
    })
    if (error) throw new Error(error.message)
    set((state) => ({
      sections: [...state.sections.filter((s) => s.id !== row.id), { ...row, content }].sort(
        (a, b) => a.order_index - b.order_index
      ),
    }))
    purgePage("section-add")
  }

  /** Delete a section row (undo of add / redo of delete). Mirrors removeSection's persistence. */
  const deleteSectionState = async (sectionId: string) => {
    set((state) => ({
      sections: state.sections
        .filter((s) => s.id !== sectionId)
        .map((s, i) => ({ ...s, order_index: i })),
    }))
    const { error } = await supabase.from("page_sections").delete().eq("id", sectionId)
    if (error) throw new Error(error.message)
    purgePage("section-delete")
  }

  /** Apply a history entry in the given direction through the normal persistence paths. */
  const applyEntry = async (entry: UndoEntry, direction: "undo" | "redo") => {
    if (entry.kind === "section") {
      const target = direction === "undo" ? entry.before : entry.after
      const other = direction === "undo" ? entry.after : entry.before
      if (target === null) {
        await deleteSectionState(entry.sectionId)
      } else if (other === null) {
        if (!entry.row) return
        await recreateSection(entry.row, target)
      } else {
        await persistSectionContent(entry.sectionId, target)
      }
      return
    }
    if (entry.kind === "order") {
      const orders = direction === "undo" ? entry.before : entry.after
      applyOrderLocal(orders)
      await persistOrderDb(orders)
      return
    }
    await applyDesignTokensState(direction === "undo" ? entry.before : entry.after)
  }

  return {
  org: null,
  customizations: null,
  pages: [],
  sections: [],
  pageId: null,
  pageSlug: null,
  pageTitle: null,
  isPublished: false,
  undoStack: [],
  redoStack: [],
  loadError: null,

  load: async (orgId: string, pageSlugOrId: string = "home") => {
    set({ loadError: null })
    const { data: orgData, error: orgError } = await supabase
      .from("organizations")
      .select("id, name, slug, mission_statement, logo_url")
      .eq("id", orgId)
      .maybeSingle()
    if (orgError || !orgData) {
      set({ loadError: orgError?.message ?? "Failed to load organization" })
      return
    }
    const org = orgData as CustomizationOrg

    const { data: custData, error: custError } = await supabase
      .from("org_customizations")
      .select("primary_color, secondary_color, font_family, heading_font, body_font, font_size, sticky_nav, template_id, nav_cta_text, custom_css, footer_content, logo_scale, design_tokens, announcement")
      .eq("org_id", orgId)
      .maybeSingle()
    if (custError) {
      set({ loadError: custError.message, org })
      return
    }
    const rawFont = (custData as { font_family?: string } | null)?.font_family
    const fontFamily =
      rawFont?.includes(",") ? rawFont : rawFont ? `${rawFont}, sans-serif` : "Inter, sans-serif"

    const custRecord = custData as Record<string, unknown> | null
    const customizations: OrgCustomizations = custData
      ? {
          ...custRecord,
          font_family: fontFamily,
          template_version: "1.0.0",
          nav_cta_text: custRecord?.nav_cta_text ?? "Donate",
        } as OrgCustomizations
      : {
          primary_color: "#3b82f6",
          secondary_color: "#1e40af",
          font_family: "Inter, sans-serif",
          template_id: null,
          template_version: "1.0.0",
          nav_cta_text: "Donate",
        } as OrgCustomizations

    const { data: allPagesData, error: pagesError } = await supabase
      .from("pages")
      .select("id, slug, title, is_published, page_type")
      .eq("org_id", orgId)
      .order("slug", { ascending: true })
    if (pagesError) {
      set({ org, customizations: custData ? customizations : null, loadError: pagesError.message })
      return
    }
    const pages: PageSummary[] = sortPages(
      ((allPagesData ?? []) as { id: string; slug: string; title: string | null; is_published: boolean; page_type?: string }[]).map(
        (p) => ({ id: p.id, slug: p.slug, title: p.title, is_published: p.is_published, page_type: p.page_type })
      )
    )

    const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      pageSlugOrId
    )
    const pageQuery = supabase
      .from("pages")
      .select("id, slug, title, is_published")
      .eq("org_id", orgId)
    const pageQueryWithFilter = isUuid
      ? pageQuery.eq("id", pageSlugOrId)
      : pageQuery.eq("slug", pageSlugOrId)
    const { data: pageData, error: pageError } = await pageQueryWithFilter.maybeSingle()
    if (pageError || !pageData) {
      set({
        org,
        customizations: custData ? customizations : null,
        pages,
        loadError: pageError?.message ?? `Page not found: ${pageSlugOrId}`,
      })
      return
    }
    const pageId = (pageData as { id: string }).id
    const pageSlug = (pageData as { slug: string }).slug
    const pageTitle = (pageData as { title: string | null }).title
    const isPublished = (pageData as { is_published: boolean }).is_published

    const { data: sectionsData, error: sectionsError } = await supabase
      .from("page_sections")
      .select("id, section_type, order_index, content, page_id, org_id, is_visible")
      .eq("page_id", pageId)
      .order("order_index", { ascending: true })
    if (sectionsError) {
      set({
        org,
        customizations,
        pages,
        pageId,
        pageSlug,
        pageTitle,
        isPublished,
        loadError: sectionsError.message,
      })
      return
    }
    // Footer is global — overlay each page's footer row with the canonical footer_content.
    const globalFooterContent = (custRecord?.footer_content as Record<string, unknown> | null | undefined) ?? null
    const sections = ((sectionsData ?? []) as PageSection[]).map((s) =>
      s.section_type === "footer" && globalFooterContent
        ? { ...s, content: globalFooterContent }
        : s
    )
    set({
      org,
      customizations,
      pages,
      sections,
      pageId,
      pageSlug,
      pageTitle,
      isPublished,
      loadError: null,
      undoStack: [],
      redoStack: [],
    })
  },

  setCurrentPage: async (page) => {
    const { org, customizations } = get()
    if (!org || !page) {
      set({ pageId: null, pageSlug: null, pageTitle: null, sections: [], isPublished: false, undoStack: [], redoStack: [] })
      return
    }
    // History is page-scoped — switching pages resets undo/redo.
    set({ pageId: page.id, pageSlug: page.slug, pageTitle: page.title, isPublished: page.is_published, undoStack: [], redoStack: [] })
    // Re-fetch footer_content so switching pages always shows the current global footer.
    const [sectionsResult, footerResult] = await Promise.all([
      supabase
        .from("page_sections")
        .select("id, section_type, order_index, content, page_id, org_id, is_visible")
        .eq("page_id", page.id)
        .order("order_index", { ascending: true }),
      supabase
        .from("org_customizations")
        .select("footer_content")
        .eq("org_id", org.id)
        .maybeSingle(),
    ])
    if (sectionsResult.error) {
      set({ loadError: sectionsResult.error.message })
      return
    }
    const globalFooterContent =
      (footerResult.data?.footer_content as Record<string, unknown> | null | undefined) ??
      (customizations as unknown as Record<string, unknown> | null)?.footer_content as Record<string, unknown> | null | undefined ??
      null
    const sections = ((sectionsResult.data ?? []) as PageSection[]).map((s) =>
      s.section_type === "footer" && globalFooterContent
        ? { ...s, content: globalFooterContent }
        : s
    )
    set({ sections, loadError: null })
  },

  addPage: (page) => {
    const { pages } = get()
    if (pages.some((p) => p.id === page.id)) return
    set({ pages: sortPages([...pages, page]) })
  },

  removePage: async (pageId) => {
    const { pages, pageId: currentPageId } = get()
    const next = pages.filter((p) => p.id !== pageId)
    set({ pages: sortPages(next) })
    if (currentPageId === pageId) {
      const fallback = next.find((p) => p.slug === "home") ?? next[0]
      if (fallback) {
        await get().setCurrentPage(fallback)
      } else {
        set({ pageId: null, pageSlug: null, pageTitle: null, sections: [], isPublished: false })
      }
    }
  },

  updateFont: async (font) => {
    const { customizations, org } = get()
    if (!customizations) {
      console.error("[customizationStore] updateFont: no customizations in state")
      throw new Error("Customizations not loaded")
    }
    const orgId = org?.id
    if (!orgId) {
      console.error("[customizationStore] updateFont: no orgId (org not loaded)")
      throw new Error("Organization not loaded. Please refresh and try again.")
    }

    const next = { ...customizations, font_family: font }
    const design_tokens = syncStoredTokensWithLegacy(next)
    set({
      customizations: { ...next, design_tokens },
    })

    const { data, error } = await supabase
      .from("org_customizations")
      .upsert(
        { org_id: orgId, font_family: font, design_tokens: design_tokens as Json },
        { onConflict: "org_id", ignoreDuplicates: false }
      )
      .select("id")

    if (error) {
      console.error("[customizationStore] updateFont failed:", error)
      throw new Error(error.message)
    }
    if (!data || data.length === 0) {
      console.error("[customizationStore] updateFont: no row upserted (RLS?)")
      throw new Error("Could not save font. Check that you have editor access to this organization.")
    }
    purge({ type: "brand-change" })
  },

  updateHeadingFont: async (font) => {
    const { customizations, org } = get()
    if (!customizations) throw new Error("Customizations not loaded")
    const orgId = org?.id
    if (!orgId) throw new Error("Organization not loaded. Please refresh and try again.")
    const next = { ...customizations, heading_font: font }
    const design_tokens = syncStoredTokensWithLegacy(next)
    set({ customizations: { ...next, design_tokens } })
    const { data, error } = await supabase
      .from("org_customizations")
      .upsert({ org_id: orgId, heading_font: font, design_tokens: design_tokens as Json }, { onConflict: "org_id", ignoreDuplicates: false })
      .select("id")
    if (error) throw new Error(error.message)
    if (!data || data.length === 0) throw new Error("Could not save heading font. Check editor access.")
    purge({ type: "brand-change" })
  },

  updateBodyFont: async (font) => {
    const { customizations, org } = get()
    if (!customizations) throw new Error("Customizations not loaded")
    const orgId = org?.id
    if (!orgId) throw new Error("Organization not loaded. Please refresh and try again.")
    const next = { ...customizations, body_font: font }
    const design_tokens = syncStoredTokensWithLegacy(next)
    set({ customizations: { ...next, design_tokens } })
    const { data, error } = await supabase
      .from("org_customizations")
      .upsert({ org_id: orgId, body_font: font, design_tokens: design_tokens as Json }, { onConflict: "org_id", ignoreDuplicates: false })
      .select("id")
    if (error) throw new Error(error.message)
    if (!data || data.length === 0) throw new Error("Could not save body font. Check editor access.")
    purge({ type: "brand-change" })
  },

  updateFontSize: async (size) => {
    const { customizations, org } = get()
    if (!customizations) throw new Error("Customizations not loaded")
    const orgId = org?.id
    if (!orgId) throw new Error("Organization not loaded. Please refresh and try again.")
    const next = { ...customizations, font_size: size }
    const design_tokens = syncStoredTokensWithLegacy(next)
    set({ customizations: { ...next, design_tokens } })
    const { error } = await supabase
      .from("org_customizations")
      .upsert({ org_id: orgId, font_size: size, design_tokens: design_tokens as Json }, { onConflict: "org_id", ignoreDuplicates: false })
    if (error) throw new Error(error.message)
    purge({ type: "brand-change" })
  },

  updateDesignTokens: async (patch) => {
    const { customizations, org } = get()
    if (!customizations) throw new Error("Customizations not loaded")
    const orgId = org?.id
    if (!orgId) throw new Error("Organization not loaded. Please refresh and try again.")

    const before = (customizations.design_tokens as StoredDesignTokens | null | undefined) ?? null
    const current = before ?? {}
    const design_tokens: StoredDesignTokens = {
      ...current,
      ...patch,
      colors: { ...current.colors, ...patch.colors },
      typography: { ...current.typography, ...patch.typography },
      spacing: { ...current.spacing, ...patch.spacing },
      shape: { ...current.shape, ...patch.shape },
    }
    pushEntry({ kind: "tokens", before, after: design_tokens, at: Date.now() })
    await applyDesignTokensState(design_tokens)
  },

  updateColor: async (key, color) => {
    const { customizations, org } = get()
    if (!customizations) {
      console.error("[customizationStore] updateColor: no customizations in state")
      throw new Error("Customizations not loaded")
    }
    const orgId = org?.id
    if (!orgId) {
      console.error("[customizationStore] updateColor: no orgId (org not loaded)")
      throw new Error("Organization not loaded. Please refresh and try again.")
    }

    const k = key === "primary" ? "primary_color" : "secondary_color"
    const next = { ...customizations, [k]: color }
    const design_tokens = syncStoredTokensWithLegacy(next)
    set({
      customizations: { ...next, design_tokens },
    })

    const { data, error } = await supabase
      .from("org_customizations")
      .upsert(
        { org_id: orgId, [k]: color, design_tokens: design_tokens as Json },
        { onConflict: "org_id", ignoreDuplicates: false }
      )
      .select("id")

    if (error) {
      console.error("[customizationStore] updateColor failed:", error)
      throw new Error(error.message)
    }
    if (!data || data.length === 0) {
      console.error("[customizationStore] updateColor: no row upserted (RLS?)")
      throw new Error("Could not save color. Check that you have editor access to this organization.")
    }
    purge({ type: "brand-change" })
  },

  updateNavCtaText: async (text) => {
    const { customizations, org } = get()
    if (!customizations) throw new Error("Customizations not loaded")
    const orgId = org?.id
    if (!orgId) throw new Error("Organization not loaded")
    set({ customizations: { ...customizations, nav_cta_text: text } })
    const { error } = await supabase
      .from("org_customizations")
      .upsert(
        { org_id: orgId, nav_cta_text: text },
        { onConflict: "org_id", ignoreDuplicates: false }
      )
    if (error) throw new Error(error.message)
    purge({ type: "brand-change" })
  },

  updateLogoScale: async (scale) => {
    const { customizations, org } = get()
    if (!customizations) throw new Error("Customizations not loaded")
    const orgId = org?.id
    if (!orgId) throw new Error("Organization not loaded")
    set({ customizations: { ...customizations, logo_scale: scale } })
    const { error } = await supabase
      .from("org_customizations")
      .upsert(
        { org_id: orgId, logo_scale: scale },
        { onConflict: "org_id", ignoreDuplicates: false }
      )
    if (error) throw new Error(error.message)
    purge({ type: "brand-change" })
  },

  updateAnnouncement: async (config) => {
    const { customizations, org } = get()
    if (!customizations) throw new Error("Customizations not loaded")
    const orgId = org?.id
    if (!orgId) throw new Error("Organization not loaded")
    set({ customizations: { ...customizations, announcement: config } })
    const { error } = await supabase
      .from("org_customizations")
      .upsert(
        { org_id: orgId, announcement: config as Json },
        { onConflict: "org_id", ignoreDuplicates: false }
      )
    if (error) throw new Error(error.message)
    purge({ type: "brand-change" })
  },

  updateStickyNav: async (sticky) => {
    const { customizations, org } = get()
    if (!customizations) throw new Error("Customizations not loaded")
    const orgId = org?.id
    if (!orgId) throw new Error("Organization not loaded")
    set({ customizations: { ...customizations, sticky_nav: sticky } })
    const { error } = await supabase
      .from("org_customizations")
      .upsert(
        { org_id: orgId, sticky_nav: sticky },
        { onConflict: "org_id", ignoreDuplicates: false }
      )
    if (error) throw new Error(error.message)
    purge({ type: "brand-change" })
  },

  updateCustomCss: async (css) => {
    const { customizations, org } = get()
    if (!customizations) {
      console.error("[customizationStore] updateCustomCss: no customizations in state")
      throw new Error("Customizations not loaded")
    }
    const orgId = org?.id
    if (!orgId) {
      console.error("[customizationStore] updateCustomCss: no orgId (org not loaded)")
      throw new Error("Organization not loaded. Please refresh and try again.")
    }

    set({
      customizations: { ...customizations, custom_css: css },
    })

    const { data, error } = await supabase
      .from("org_customizations")
      .upsert(
        { org_id: orgId, custom_css: css },
        { onConflict: "org_id", ignoreDuplicates: false }
      )
      .select("id")

    if (error) {
      console.error("[customizationStore] updateCustomCss failed:", error)
      throw new Error(error.message)
    }
    if (!data || data.length === 0) {
      console.error("[customizationStore] updateCustomCss: no row upserted (RLS?)")
      throw new Error("Could not save CSS. Check that you have editor access to this organization.")
    }
    purge({ type: "brand-change" })
  },

  setOrgLogo: (url) => {
    const { org } = get()
    if (!org) return
    set({ org: { ...org, logo_url: url } })
  },

  updateSection: async (sectionId, content) => {
    const { sections } = get()
    const section = sections.find((s) => s.id === sectionId)
    const before = (section?.content as Record<string, unknown>) ?? {}
    const newContent = { ...before, ...content }
    pushEntry({ kind: "section", sectionId, before, after: newContent, at: Date.now() })
    await persistSectionContent(sectionId, newContent)
  },

  reorderSections: async (newOrder) => {
    const { sections } = get()
    const before = sections.map((s) => ({ id: s.id, order_index: s.order_index }))
    const reindexed = newOrder.map((s, i) => ({ ...s, order_index: i }))
    const after = reindexed.map((s) => ({ id: s.id, order_index: s.order_index }))
    // Record final orders only — never the intermediate negative-index phase.
    if (JSON.stringify(before) !== JSON.stringify(after)) {
      pushEntry({ kind: "order", before, after, at: Date.now() })
    }
    set({ sections: reindexed })
    await persistOrderDb(after)
  },

  addSection: async (sectionType, afterIndex) => {
    const { sections, pageId, org } = get()
    if (!pageId || !org) return null
    const { getDefaultBlockContent } = await import("@/components/website-builder/blocks/registry")
    let defaultContent: Record<string, unknown>
    try {
      defaultContent = getDefaultBlockContent(sectionType as "hero") as Record<string, unknown>
    } catch (e) {
      throw new Error(
        `Unknown section type "${sectionType}". ${e instanceof Error ? e.message : String(e)}`
      )
    }
    // BUILDER_DESIGN_SPEC Section 17: Impact Stats and Team Members get columns=3, all others 1
    const columns =
      sectionType === "impact_stats" || sectionType === "team_members" ? 3 : 1
    defaultContent = { ...defaultContent, columns }
    const insertAt = Math.min(afterIndex + 1, sections.length)
    const { data, error } = await supabase
      .from("page_sections")
      .insert({
        page_id: pageId,
        org_id: org.id,
        section_type: sectionType,
        order_index: sections.length,
        content: defaultContent as unknown as Json,
        is_visible: true,
      })
      .select("id, section_type, order_index, content, page_id, org_id, is_visible")
      .single()
    if (error) throw new Error(error.message)
    const newSection = { ...(data as PageSection), is_visible: (data as { is_visible?: boolean })?.is_visible ?? true }
    const sorted = [...sections].sort((a, b) => a.order_index - b.order_index)
    const newOrder = [...sorted.slice(0, insertAt), newSection, ...sorted.slice(insertAt)]
      .map((s, i) => ({ ...s, order_index: i }))
    const insertedRow = newOrder.find((s) => s.id === newSection.id) ?? newSection
    // One history entry for the whole add (before: null = section did not exist).
    pushEntry({
      kind: "section",
      sectionId: newSection.id,
      before: null,
      after: (insertedRow.content as Record<string, unknown>) ?? {},
      row: insertedRow,
      at: Date.now(),
    })
    set({ sections: newOrder })
    await persistOrderDb(newOrder.map((s) => ({ id: s.id, order_index: s.order_index })))
    return newSection
  },

  removeSection: async (sectionId) => {
    const { sections } = get()
    const target = sections.find((s) => s.id === sectionId)
    if (target) {
      // after: null = deleted; row snapshot allows recreation on undo.
      pushEntry({
        kind: "section",
        sectionId,
        before: (target.content as Record<string, unknown>) ?? {},
        after: null,
        row: target,
        at: Date.now(),
      })
    }
    await deleteSectionState(sectionId)
  },

  toggleSectionVisibility: async (sectionId, visible) => {
    get()
    set((state) => ({
      sections: state.sections.map((s) =>
        s.id === sectionId ? { ...s, is_visible: visible } : s
      ),
    }))
    const { error } = await supabase
      .from("page_sections")
      .update({ is_visible: visible })
      .eq("id", sectionId)
    if (error) throw new Error(error.message)
    purgePage("section-visibility")
  },

  /** NOS-116: Mark section as reviewed — clears import_source so badge is removed */
  markSectionReviewed: async (sectionId) => {
    const { sections } = get()
    const section = sections.find((s) => s.id === sectionId)
    if (!section) return
    const content = { ...(section.content as Record<string, unknown> ?? {}) }
    delete content.import_source
    set((state) => ({
      sections: state.sections.map((s) =>
        s.id === sectionId ? { ...s, content } : s
      ),
    }))
    const { error } = await supabase
      .from("page_sections")
      .update({ content: content as Json })
      .eq("id", sectionId)
    if (error) throw new Error(error.message)
    purge({
      type: "section-save",
      pageSlug: get().pageSlug ?? "home",
      sectionType: section.section_type,
    })
  },

  publishWebsite: async () => {
    const { pageId } = get()
    if (!pageId) throw new Error("No page selected. Make sure you're on the Home page.")
    const { data, error } = await supabase
      .from("pages")
      .update({ is_published: true })
      .eq("id", pageId)
      .select("id")
    if (error) throw new Error(error.message)
    if (!data || data.length === 0) {
      throw new Error(
        "Publish failed. You may not have permission to publish, or the page was not found."
      )
    }
    set({ isPublished: true })
    purge({ type: "page-publish", pageSlug: get().pageSlug ?? "home" })
  },

  unpublishPage: async () => {
    const { pageId } = get()
    if (!pageId) throw new Error("No page selected.")
    const { error } = await supabase
      .from("pages")
      .update({ is_published: false })
      .eq("id", pageId)
    if (error) throw new Error(error.message)
    set({ isPublished: false })
    purge({ type: "page-unpublish", pageSlug: get().pageSlug ?? "home" })
  },

  pushUndoHistory: (sectionId, content) => {
    // Pre-change snapshot: recorded as a no-op edit that the following updateSection
    // coalesces into, keeping this snapshot as the entry's `before`.
    pushEntry({
      kind: "section",
      sectionId,
      before: { ...content },
      after: { ...content },
      at: Date.now(),
    })
  },

  undo: async () => {
    const { undoStack, redoStack } = get()
    const entry = undoStack[undoStack.length - 1]
    if (!entry) return
    set({ undoStack: undoStack.slice(0, -1), redoStack: [...redoStack, entry] })
    await applyEntry(entry, "undo")
  },

  redo: async () => {
    const { undoStack, redoStack } = get()
    const entry = redoStack[redoStack.length - 1]
    if (!entry) return
    set({ redoStack: redoStack.slice(0, -1), undoStack: [...undoStack, entry].slice(-MAX_HISTORY) })
    await applyEntry(entry, "redo")
  },

  // ─── NOS-158: theme preview / commit ─────────────────────────────────────

  themePreviewActive: false,

  previewTheme: (pkg) => {
    const { customizations, sections } = get()
    if (!customizations) return
    // First preview snapshots the pristine state; re-previews plan against it
    // so hovering across themes never compounds.
    if (!themeBackup) themeBackup = { customizations, sections }
    const base = themeBackup
    const tokens = pkg.tokens ?? null
    const legacyCols = tokens ? legacyColumnsFromTokens(tokens) : {}
    const updates = planThemeApplication(pkg, base.sections as ExistingSection[])
    const byId = new Map(updates.map((u) => [u.id, u.content]))
    set({
      customizations: {
        ...base.customizations,
        ...legacyCols,
        ...(tokens ? { design_tokens: tokens } : { design_tokens: base.customizations.design_tokens }),
      },
      sections: base.sections.map((s) => (byId.has(s.id) ? { ...s, content: byId.get(s.id)! } : s)),
      themePreviewActive: true,
    })
  },

  cancelThemePreview: () => {
    if (!themeBackup) return
    set({
      customizations: themeBackup.customizations,
      sections: themeBackup.sections,
      themePreviewActive: false,
    })
    themeBackup = null
  },

  commitTheme: async (pkg, templateId) => {
    // Commit from the pristine state so persisted diffs are real, not preview deltas.
    get().cancelThemePreview()
    const { customizations, sections, org } = get()
    if (!customizations) throw new Error("Customizations not loaded")
    const orgId = org?.id
    if (!orgId) throw new Error("Organization not loaded. Please refresh and try again.")

    // Tokens: the theme's set replaces stored tokens (art direction is authoritative);
    // undoable via the same tokens entry the Brand Panel uses.
    if (pkg.tokens) {
      const before = (customizations.design_tokens as StoredDesignTokens | null) ?? null
      pushEntry({ kind: "tokens", before, after: pkg.tokens, at: Date.now() })
      await applyDesignTokensState(pkg.tokens)
    }

    if (templateId) {
      const { error } = await supabase
        .from("org_customizations")
        .upsert({ org_id: orgId, template_id: templateId }, { onConflict: "org_id", ignoreDuplicates: false })
      if (error) throw new Error(error.message)
    }

    // Current page: variant switches go through updateSection so each is undoable.
    const updates = planThemeApplication(pkg, get().sections as ExistingSection[])
    for (const update of updates) {
      await get().updateSection(update.id, update.content)
    }

    // Every other page: same variant mapping, persisted directly (their undo
    // stacks are page-scoped and reset on page switch anyway).
    const err = await applyThemeVariants(orgId, pkg, {
      excludeSectionIds: sections.map((s) => s.id),
    })
    if (err) throw err
    // Theme swap restyles every page (tokens + per-page variants) → org-wide purge.
    purge({ type: "theme-change" })
  },
  }
})
